import { useMsal } from '@azure/msal-react';
import { useCallback, useMemo, useState } from 'react';

import {
  AuthenticationResult,
  InteractionRequiredAuthError,
  InteractionStatus,
} from '@azure/msal-browser';
import noop from 'lodash/noop';

import { useEnvironmentContext } from '@sbv-clients/data-access-api';

import { useAuth } from '../context/auth';

export const useSSoLogin = () => {
  const [authInProgress, setAuthInProgress] = useState(false);
  const { onAuthSuccess } = useAuth();
  const { instance, inProgress } = useMsal();

  const { azureSSOConfig } = useEnvironmentContext();

  const loginRequest = useMemo(() => {
    return {
      scopes: [
        'openid',
        'profile',
        'email',
        azureSSOConfig?.NX_APP_AZURE_AD_API_SCOPE as string,
      ],
    };
  }, [azureSSOConfig?.NX_APP_AZURE_AD_API_SCOPE]);

  const loginWithPopup = useCallback(async () => {
    const response: AuthenticationResult = await instance.loginPopup(
      loginRequest
    );
    const silentTokenResponse = await instance.acquireTokenSilent({
      ...loginRequest,
      account: response.account,
    });
    localStorage.setItem('OIDC', JSON.stringify(silentTokenResponse));
    if (silentTokenResponse.accessToken) {
      onAuthSuccess(silentTokenResponse.accessToken, '');
    }
  }, [instance, loginRequest, onAuthSuccess]);

  const acquireTokenPopup = useCallback(async () => {
    await instance
      .acquireTokenPopup(loginRequest)
      .then((authenticationResult) => {
        localStorage.setItem('OIDC', JSON.stringify(authenticationResult));
        if (authenticationResult.accessToken) {
          onAuthSuccess(authenticationResult.accessToken, '');
        }
      })
      .catch(noop);
  }, [instance, loginRequest, onAuthSuccess]);

  const onLogin = async () => {
    if (inProgress !== InteractionStatus.None) {
      return null;
    }
    setAuthInProgress(true);
    try {
      await instance.initialize();
      await loginWithPopup();
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await acquireTokenPopup();
      }
    } finally {
      setAuthInProgress(false);
    }
  };

  return {
    onLogin,
    authInProgress,
  };
};

export default useSSoLogin;
